import React, { useState, useEffect, forwardRef, useImperativeHandle, useContext } from "react";
import { css } from "@emotion/css";
import { fetchTiles, ISlUserTiles, userTypeIndex, ITile } from "./SLDashboardTilesRequests";
import { Pivot, PivotItem, PivotLinkFormat, PivotLinkSize, Text } from "@fluentui/react";
import LoadingSpinner from "../../baseComponents/LoadingSpinner";
import { INotificationMessagesRef } from "../NotificationMessages";
import { OrderedMap } from "immutable";
import { useHistory, useLocation } from "react-router-dom";
import { getStateObjects } from "../../services/UrlHelpers";
import { UserContext } from "../../contexts/UserContext";
import { LoggingContext } from "../../contexts/LoggingContext";
import { FatalError } from "../../services/ErrorHelpers";

export enum TilesLoadingStatus {
  idle = "idle",
  pending = "pending",
  resolved = "resolved",
  rejected = "rejected"
}

interface IDashboardTilesProps {
  switchGridTileFn: (tileName: userTypeIndex | "", names: ActionId[], clearNotifications: boolean) => void;
  notificationsFn: INotificationMessagesRef["setMessage"];
}

export enum ActionId {
  SendToPoster = 1,
  Attachments = 2,
  Delete = 3,
  Recall = 4,
  AddPosters = 5,
  SubmitForReview = 6,
  ApproveSLEntries = 7,
  NeedsClarification = 8,
  ExportToExcel = 9
}

const styles = css`
  .tile-styles .ms-Button {
    width: 150px;
    height: 150px;
    margin: 5px;
    text-align: center;
    border: solid 1px var(--main-bg-color, lightgray);

    &:hover {
      background-color: var(--main-bg-color, gray);
      border: none;
    }

    &[aria-selected="true"] {
      background-color: var(--main-bg-color, gray);
      border: none;
    }
  }

  .tile-styles {
    overflow: auto;
  }

  .tile-styles .ms-Button .ms-Pivot-linkContent {
    position: relative;
    height: 125px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    align-content: space-between;
    text-align: center;
    margin: auto;
  }

  .tile-styles .ms-Button-flexContainer {
    align-items: flex-start;
  }

  .tile-styles .ms-Button .ms-Pivot-text {
    align-self: stretch;
    width: 100%;
    flex-basis: 100%;
    line-height: 1.5;
    min-height: 75px;
    overflow: visible;
    white-space: pre-wrap;
    word-break: break-word;
    white-space: normal;
  }

  .tile-styles .ms-Button .ms-Pivot-count {
    align-self: center;
    margin: 0px;
  }
`;

export interface ISLDashboardTileRef {
  refreshTiles: (currentTile: userTypeIndex) => any;
}

const specificOrder: userTypeIndex[] = [
  "Sign Off Pending",
  "My Pending Clarifications",
  "Awaiting Clarifications",
  "Ready To Post",
  "Sign Off Requested",
  "Send To Poster",
  "My Draft's"
];

interface IIndexedTile extends ITile {
  tileIndex: number;
}

interface ITileState {
  tilesForPivot: IPivotItem[];
  currentTile: userTypeIndex | "";
  tiles: OrderedMap<string, IIndexedTile>;
  userHasNoData: boolean;
}

interface IPivotItem {
  count: number;
  name: string;
}

// Calculate tiles (remove tiles with 0 count)
// Calculate indices of tiles (use an OrderedDict if necessary)
// Get the currentTile from location or first tile
function processTiles(theTiles: ISlUserTiles) {
  let tiles = OrderedMap({});
  const tilesForPivot = [];
  let index = 0;
  for (const theTileName of specificOrder) {
    if (theTileName in theTiles) {
      const tileObj = { ...theTiles[theTileName], tileIndex: index };
      tiles = tiles.set(theTileName, tileObj);
      tilesForPivot.push({
        name: theTileName,
        count: tileObj.tileCount
      });
      index += 1;
    }
  }

  return {
    tiles: tiles as OrderedMap<string, IIndexedTile>,
    tilesForPivot: tilesForPivot as IPivotItem[]
  };
}

function getCurrentTile(theTiles: ReturnType<typeof processTiles>, location: ReturnType<typeof useLocation>) {
  const state = getStateObjects(location);
  let tileStr = theTiles.tilesForPivot[0].name;
  if (state && state.state) {
    const possibleTile = state.state.tileName;
    if (possibleTile) {
      tileStr = possibleTile;
    }
    if (!tileStr) {
      tileStr = "My Draft's";
    }
  }
  return tileStr as userTypeIndex;
}

// on mount process:
// get the tiles
// calculate indices
// get tile from location or first tile in indices
// switch grid

// on click process:
// get tilename
// set location on router
// switch grid

// on refresh tiles process:
// get tilename, set location on router
// get the tiles
// calculate indices
// switch grid

// when to switch grid? When TileState changes
// what is tile state? Tile Name, Tiles, and Tile Indices

const initialTileState: ITileState = {
  tilesForPivot: [],
  currentTile: "",
  tiles: OrderedMap({
    "My Draft's": { tileCount: 0, tileIndex: 0, tileName: "", tileDisplayName: "", allowActionLinks: [] },
    "Send To Poster": { tileCount: 0, tileIndex: 1, tileName: "", tileDisplayName: "", allowActionLinks: [] },
    "Sign Off Requested": { tileCount: 0, tileIndex: 2, tileName: "", tileDisplayName: "", allowActionLinks: [] },
    "My Pending Clarifications": {
      tileCount: 0,
      tileIndex: 3,
      tileName: "",
      tileDisplayName: "",
      allowActionLinks: []
    },
    "Ready To Post": { tileCount: 0, tileIndex: 4, tileName: "", tileDisplayName: "", allowActionLinks: [] },
    "Sign Off Pending": { tileCount: 0, tileIndex: 5, tileName: "", tileDisplayName: "", allowActionLinks: [] },
    "Awaiting Clarifications": { tileCount: 0, tileIndex: 6, tileName: "", tileDisplayName: "", allowActionLinks: [] }
  }),
  userHasNoData: false
};

const SLDashboardTiles = forwardRef((props: IDashboardTilesProps, ref: React.Ref<ISLDashboardTileRef>) => {
  const { appInsights } = useContext(LoggingContext);
  if (!appInsights) {
    throw new FatalError("Please setup ApplicationInsights Provider for SLDashboardTiles");
  }
  const { accessToken } = useContext(UserContext);
  const history = useHistory();
  const location = useLocation();
  const [tilesLoadingStatus, setLoadingStatus] = useState<TilesLoadingStatus>(TilesLoadingStatus.idle);
  const [tileState, setTileState] = useState<ITileState>(initialTileState);

  useEffect(() => {
    setLoadingStatus(TilesLoadingStatus.resolved);
    const currentTileName = tileState.currentTile;
    if (tileState.currentTile === "") {
      props.switchGridTileFn(currentTileName, [], false);
    }
    const theTile = tileState.tiles.get(currentTileName);
    if (tileState && theTile) {
      props.switchGridTileFn(currentTileName, theTile.allowActionLinks, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileState]);

  useEffect(() => {
    let mounted = true;
    setLoadingStatus(TilesLoadingStatus.pending);
    const loadAndProcessTiles = async () => {
      try {
        const tilesFromApi = await fetchTiles(accessToken);
        if (tilesFromApi) {
          const tileConfiguration = processTiles(tilesFromApi);
          const theTile = getCurrentTile(tileConfiguration, location);
          if (mounted) {
            setTileState({ ...tileConfiguration, currentTile: theTile, userHasNoData: false });
          }
        } else {
          if (mounted) {
            setTileState({ ...initialTileState, userHasNoData: true });
          }
        }
      } catch (e) {
        console.error(e);
        appInsights.trackException({ exception: e });
        if (mounted) {
          setLoadingStatus(TilesLoadingStatus.rejected);
        }
      }
    };
    loadAndProcessTiles();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (item?: PivotItem) => {
    if (item !== undefined) {
      const tileName = item.props.headerText as userTypeIndex;
      history.replace("/sldashboard", { tileName });
      setTileState((p) => ({
        ...p,
        currentTile: tileName
      }));
    }
  };

  useImperativeHandle(ref, () => ({
    refreshTiles(theTile: userTypeIndex) {
      setLoadingStatus(TilesLoadingStatus.pending);
      const loadAndProcessTiles = async () => {
        try {
          const tilesFromApi = await fetchTiles(accessToken);
          if (tilesFromApi) {
            const tileConfiguration = processTiles(tilesFromApi);
            const theCurrentTile = tileConfiguration.tiles.has(theTile)
              ? theTile
              : tileConfiguration.tilesForPivot[0].name || "";
            setTileState({
              ...tileConfiguration,
              currentTile: theCurrentTile as userTypeIndex,
              userHasNoData: false
            });
          } else {
            setTileState({ ...initialTileState, userHasNoData: true });
          }
        } catch (e) {
          console.error(e);
          appInsights.trackException({ exception: e });
          setLoadingStatus(TilesLoadingStatus.rejected);
        }
      };
      loadAndProcessTiles();
    }
  }));

  if (tilesLoadingStatus === TilesLoadingStatus.idle) {
    return null;
  }

  if (tilesLoadingStatus === TilesLoadingStatus.pending) {
    return <LoadingSpinner label="Loading tiles" />;
  }

  if (tilesLoadingStatus === TilesLoadingStatus.resolved && tileState.userHasNoData) {
    return (
      <h2
        className={css`
          margin: 16px 0;
          font-size: 3em;
          font-weight: 100;
          color: var(--main-font-color, black);
          text-align: center;
        `}
      >
        <Text variant={"xxLarge"} block>
          There are no pending entries.
        </Text>
      </h2>
    );
  }

  const currentTile = tileState.tiles.get(tileState.currentTile);
  let currentTileIndex = 0;
  if (currentTile) {
    currentTileIndex = currentTile.tileIndex;
  }

  return (
    <div className={styles}>
      <Pivot
        selectedKey={`${currentTileIndex}`}
        linkSize={PivotLinkSize.large}
        linkFormat={PivotLinkFormat.tabs}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onLinkClick={handleClick}
        className="tile-styles"
      >
        {tileState.tilesForPivot.map((value, i: number) => (
          <PivotItem headerText={value.name} key={i} itemCount={value.count} />
        ))}
      </Pivot>
    </div>
  );
});

SLDashboardTiles.displayName = "SLDashboardTiles";

export default SLDashboardTiles;
